import React, { useState, useEffect, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ExamProvider } from './contexts/ExamContext';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import { initAnalytics } from './lib/analytics';
import { IS_APP_BUILD } from './lib/buildTarget';
import { CONTENT_CLEARANCE_CLASS, CONTENT_HEIGHT_CLASS } from './lib/bottomNavMetrics';
// dashboard-rediseno (4.1 y 4.2): el alto de la barra, el ancho del menu y el relleno del contenido salen de la
// fuente unica del chasis; aqui ya no se escribe ningun numero a mano.
import { CONTENIDO_DEBAJO_DE_LA_BARRA_CLASS, CONTENIDO_JUNTO_AL_MENU_CLASS, RELLENO_DEL_CONTENIDO_CLASS, anunciarBarra } from './lib/medidasDelChasis';
import Sidebar from './components/Sidebar';
import ExamLockedRoute from './components/ExamLockedRoute';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AuthCallback from './pages/AuthCallback';
import AppleAuthCallback from './pages/AppleAuthCallback';
import Dashboard from './pages/Dashboard';
import PendingPage from './pages/PendingPage';
import StudyCenter from './pages/StudyCenter';
// Lazy-loaded so KaTeX (~250KB) ships only in the study-route chunks, not the
// initial/landing bundle. These three are the only surfaces that render math.
const ExamSimulator = React.lazy(() => import('./pages/ExamSimulator'));
const Flashcards = React.lazy(() => import('./pages/Flashcards'));
const AdaptiveStudy = React.lazy(() => import('./pages/AdaptiveStudy'));
// tutor-1 (C): la pagina del Tutor, perezosa en la web. En la app es una raiz del carril (PagerRaices la importa
// directo, como a sus hermanas); KaTeX sigue fuera del paquete inicial porque su pintor pide `MathText` en diferido.
const TutorPage = React.lazy(() => import('./pages/TutorPage'));
import Analytics from './pages/Analytics';
import PricingPage from './pages/PricingPage';
import PublicPricingPage from './pages/PublicPricingPage';
// post-t7-2a (bloque F, diseno 3.4 a): `/es` y `/es/precios`, la portada y los precios con el
// idioma forzado a espanol en la web; en la app rebotan como `/` y `/pricing`. Ver el componente.
import { PortadaEnEspanol, PreciosEnEspanol } from './components/RutasEnEspanol';
import StudyLog from './pages/StudyLog';
import AdminErrorsPage from './pages/AdminErrorsPage';
import AdminDashboard from './pages/AdminDashboard';
import SettingsPage from './pages/SettingsPage';
// Remate de post-t7-2c (R1, "1. si" de Carlos del 15-oct): "Gana creditos gratis", la pantalla del cuadro de opinion.
import EarnCreditsPage from './pages/EarnCreditsPage';
// PILOTO-1 (firma #79): las dos pantallas de profesor y el aterrizaje del link.
import GroupsPage from './pages/GroupsPage';
import GroupDetailPage from './pages/GroupDetailPage';
// PIPE-INST T4 (firma #118) — la pantalla de temas del profesor.
import GroupBankTemasPage from './pages/GroupBankTemasPage';
// T7 tanda 2a (firma #516): el avance del grupo, pagina propia del dueno del grupo.
import GroupAvancePage from './pages/GroupAvancePage';
// PIPE-INST T5.1 — LA CURACION VIAJA LAZY, y por la MISMA razon que las tres de
// estudio (ver el comentario de arriba): desde T5.1 esta pantalla renderiza
// formulas con `MathText`, asi que importarla de forma estatica metia KaTeX —su
// JS y su hoja— en el bundle INICIAL. Medido con `craco build`: +76.4 kB de JS y
// +4.39 kB de CSS gzip que pagaba TODO el mundo, incluido el alumno de telefono
// que nunca vera una pantalla de profesor. Con `React.lazy` KaTeX vuelve a
// viajar solo en el chunk de quien lo necesita, que es la invariante que este
// archivo ya declaraba.
// NO entra a `IdleRoutePrefetch`: ese prefetch existe para las pantallas que
// CUALQUIER usuario con sesion va a abrir, y esta la abre solo un profesor
// institucional. Precargarla para todos seria devolver el problema por la puerta
// de atras. El `<Suspense>` del router ya le da su silueta.
const GroupBankCuracionPage = React.lazy(() => import('./pages/GroupBankCuracionPage'));
// estilos-1 (Bloque F): "Ver como se ve", la pagina del admin que pinta las formulas antes y despues con KaTeX.
// Perezosa por la misma razon que las de estudio: KaTeX no viaja en el paquete de entrada.
const AdminFormulasPage = React.lazy(() => import('./pages/AdminFormulasPage'));
import JoinGroupPage from './pages/JoinGroupPage';
// LAPIDA post-t7-2c (E1): murio `ReferralLandingPage`; /r/CODIGO rebota al registro (ver su ruta).
import ManageDocuments from './pages/ManageDocuments';
import ContactPage from './pages/ContactPage';
import NotificationListener from './components/NotificationListener';
import PagerRaices from './components/PagerRaices';
// tutor-1 (C): el estado del tutor de la sesion (una llamada a /tutor/estado al tener usuario).
import { cargarTutorSiFalta, olvidarTutor } from './lib/tutorDeLaSesion';
import DiagReadyChainPage from './pages/DiagReadyChainPage';
import BarraSuperior from './components/BarraSuperior';
import MobileBottomNav from './components/MobileBottomNav';
// LAPIDA post-t7-2c (E1): murio `LetreroVista`, el letrero del modo vista de referidos.
// PILOTO-1B (firma #80): el empujón de «te está esperando tu grupo». Vive en
// el chasis autenticado —AGUAS ABAJO del login, que no se toca— y se pinta a lo
// más UNA vez por aterrizaje; sin código pendiente devuelve null y no cuesta
// ni una petición. Ver components/PendingGroupJoinModal.js.
import PendingGroupJoinModal from './components/PendingGroupJoinModal';
// PIPE-INST T4.5: el aviso de «tu documento ya esta listo». Va arriba, hermano
// de NotificationListener y NO dentro de AppLayout, porque el logo del header
// navega fuera del chasis y lo desmontaba. El porque completo, en el componente.
// Sin bandera institucional devuelve null y no pide ni una vez.
//
// OJO AL SIGUIENTE QUE TOQUE ESTE ARCHIVO: aqui NO se pueden poner comentarios de
// bloque. Mas abajo hay dos `/admin/*` y `/diag/*` dentro de comentarios de linea
// que dejan sendos `/*` huerfanos, y `settingsExamLock.test.js` limpia bloques
// antes de leer las rutas: un `/* */` nuevo les da pareja y el barrido se come el
// registro de rutas entero. Comentarios de LINEA, como este.
import GroupBankVigilante from './components/GroupBankVigilante';
// NOTIFICACIONES-1 (firma #464): el puente del toque de una notificacion push al
// router. Hermano de NotificationListener por la misma razon que el vigilante de
// arriba: tiene que vivir en TODA pantalla. No pinta nada.
import PushPuente from './components/PushPuente';
// Guards + gate de arranque viven en routeGuards.js (F4.3b) — testeables sin
// importar toda la app. El gate pinta logo, no spinner.
import { ProtectedRoute, AdminRoute, InstitutionalRoute, PublicRoute, AuthBootGate } from './components/routeGuards';
import { PageSkeleton, DashboardSkeleton } from './components/skeletons';
// post-t7-2c (F2): la red contra las caidas, alrededor de TODO el arbol (ver el componente).
import RedDeArranque, { PuenteDeSalida } from './components/RedDeArranque';
import { invalidateScreenData } from './lib/screenDataCache';
// LAPIDA post-t7-2c (E1): murio el reclamo silencioso del codigo de invitacion (`ReclamoDeReferido`).

// Precarga en idle de los chunks lazy del shell autenticado (los 3 de estudio,
// los únicos React.lazy). Son ESTÁTICOS servidos por el CDN — costo backend
// cero — y solo se piden con sesión activa (el visitante anónimo de la landing
// no paga bytes de estudio). Resultado: navegar a cualquier pantalla de estudio
// nunca espera red por el chunk (el fallback de Suspense casi no se ve).
function IdleRoutePrefetch() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return undefined;
    const prefetch = () => {
      import('./pages/ExamSimulator');
      import('./pages/Flashcards');
      import('./pages/AdaptiveStudy');
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 1500); // Safari/WebKit: sin requestIdleCallback
    return () => clearTimeout(id);
  }, [user]);
  return null;
}

// tutor-1 (C): la carga del estado del tutor. UNA llamada a /tutor/estado al tener usuario (el menu lateral y la
// barra de abajo pintan el renglon Tutor con ese dato); al cambiar de cuenta o al salir del chasis, se olvida.
function CargaDelTutor() {
  const { user } = useAuth();
  const idDelUsuario = user?.id;
  useEffect(() => {
    if (user) cargarTutorSiFalta(user);
    else olvidarTutor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDelUsuario]);
  useEffect(() => () => olvidarTutor(), []);
  return null;
}

// post-t7-2c (F1, Anexo D.3): `esqueleto` es el chasis mientras la sesion resuelve (solo web; ver
// `esqueletoDelArranque`). Pinta la cabecera y el `<main>` de siempre, en el mismo sitio, y deja fuera lo que pide
// datos de la sesion antes de conocerla (el menu del usuario, la pildora de sin conexion, el menu lateral, la barra
// de abajo y el empujon del grupo): entran al resolver, sin mover el contenido (son fijos).
function AppLayout({ children, esqueleto = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Listen for bottom nav "More" tap
  useEffect(() => {
    const handler = () => setSidebarOpen(true);
    document.addEventListener('toggle-sidebar', handler);
    return () => document.removeEventListener('toggle-sidebar', handler);
  }, []);

  // dashboard-rediseno (firma 6): la barra de arriba es `BarraSuperior` (logo, sin conexion, creditos, plan,
  // nombre y avatar). En modo esqueleto pinta solo el logo, en el mismo sitio, como antes la cabecera. Se anuncia
  // en `lib/medidasDelChasis.js` para que los avisos flotantes salgan 16 px debajo de ella.
  useEffect(() => anunciarBarra(), []);

  return (
    <div className="h-dvh overflow-hidden bg-[#0e0e0e]">
      <BarraSuperior esqueleto={esqueleto} />

      {/* Sidebar (fixed on desktop, overlay on mobile) */}
      {!esqueleto && <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

      {/* Mobile sidebar backdrop. z-[55]: above the header and bottom nav (both z-50)
          so the whole page dims and the drawer (z-[60]) is the only live surface —
          before, the bottom nav floated above the scrim and painted OVER the drawer's
          session block. cursor-pointer: WebKit only synthesizes click on "clickable"
          elements, so without it tapping the scrim did nothing on iOS. */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-[55] cursor-pointer"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Scrollable content area. El colchón de abajo sale de la fuente única
          del alto del nav (lib/bottomNavMetrics) — aquí ya no se escribe
          ningún número a mano. remates-2 (E): el ALTO tambien sale de ahi; en la
          web del telefono el area termina arriba de la barra de abajo. */}
      <main
        className={`${CONTENIDO_DEBAJO_DE_LA_BARRA_CLASS} ${CONTENIDO_JUNTO_AL_MENU_CLASS} ${CONTENT_HEIGHT_CLASS} overflow-y-auto overscroll-contain ${RELLENO_DEL_CONTENIDO_CLASS} ${CONTENT_CLEARANCE_CLASS} lg:pb-8`}
        data-testid="content-area"
      >
        {children}
      </main>

      {!esqueleto && <MobileBottomNav />}
      {!esqueleto && <PendingGroupJoinModal />}
      {!esqueleto && <CargaDelTutor />}
    </div>
  );
}

// post-t7-2c (F1, Anexo D.3): LAS RUTAS QUE TIENEN ESQUELETO DE ARRANQUE (solo web). Mientras la sesion resuelve,
// `ProtectedRoute` pinta el chasis en modo esqueleto con: el Panel, su `DashboardSkeleton` (el mismo que el Panel
// pinta mientras carga); la pagina del grupo y el panel del profe, la MISMA pagina en modo `soloEsqueleto`
// (`PAGINA_SIN_DATOS`: la pinta `InstitutionalRoute` sin pedir datos y, al resolver, la misma instancia pide lo
// suyo). Las demas rutas pintan el fondo de la casa, sin spinner. La app no usa nada de esto (el logo, como hoy).
const PAGINA_SIN_DATOS = 'pagina';
const ESQUELETOS_DEL_ARRANQUE = {
  '/dashboard': <DashboardSkeleton />,
  '/groups/:groupId': PAGINA_SIN_DATOS,
  '/groups/:groupId/avance': PAGINA_SIN_DATOS,
};
function esqueletoDelArranque(r) {
  const esqueleto = ESQUELETOS_DEL_ARRANQUE[r.path];
  if (!esqueleto) return undefined;
  return <AppLayout esqueleto>{esqueleto === PAGINA_SIN_DATOS ? r.el : esqueleto}</AppLayout>;
}

function AppRoutes() {
  const location = useLocation();
  // Reddit Pixel: SPA route changes don't reload the page, so fire PageVisit per
  // navigation. Skip the first render — the index.html snippet already fired the
  // initial PageVisit; without the skip it would double-count. window.rdt is absent
  // outside production (hostname gate) or with adblockers — never throw.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (window.rdt) window.rdt('track', 'PageVisit');
  }, [location.pathname]);
  // Al SALIR de una sesión de estudio, los datos derivados de sesiones
  // (historial, contador del Centro de Estudio, analíticas) cambiaron en el
  // backend. El motor de estudio es NO-TOCAR, así que la señal es la propia
  // navegación: se invalida aquí para que la siguiente visita muestre skeleton
  // + dato fresco en vez de un cache que ya miente.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    const wasStudySession = ['/study/exam', '/study/flashcards', '/study/adaptive'].some(p => prev.startsWith(p));
    if (wasStudySession && prev !== location.pathname) {
      invalidateScreenData('study-log', 'study-center', 'analytics-overview');
    }
  }, [location.pathname]);
  return (
    // Fallback de chunk lazy: silueta, no spinner (regla F4.3b). Con el
    // prefetch en idle de IdleRoutePrefetch este fallback casi nunca llega a verse.
    <Suspense fallback={<PageSkeleton />}>
    <Routes>
      {/* In the Capacitor app build, "/" is not the marketing landing — it hands off
          to the authenticated app. A logged-out user lands on /signup through the
          EXISTING guard chain (/dashboard is a ProtectedRoute; in the app build it
          redirects to /signup — SIGNUP-first, F4.3b) — no new guard is introduced.
          IS_APP_BUILD is a RUNTIME
          gate (a cross-module const the minifier does not fold): both branches ship in
          both bundles and the unused one travels inert. What stays identical on the web
          is the BEHAVIOR — it always renders <LandingPage /> — not the bundle bytes. */}
      <Route path="/" element={IS_APP_BUILD ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/pricing" element={<PublicPricingPage />} />
      <Route path="/es" element={<PortadaEnEspanol />} />
      <Route path="/es/precios" element={<PreciosEnEspanol />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Google OAuth return leg. Rendered directly (no PublicRoute guard), matching
          the old hash-detection behavior: AuthCallback must always run — PublicRoute
          would bounce a lingering registered session to /dashboard before the code
          exchange could happen. */}
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      {/* AS-4 — retorno de Apple, con el MISMO trato que el de Google (sin PublicRoute
          y sin gate de build): la página debe correr siempre, y una PublicRoute
          rebotaría una sesión vieja a /dashboard antes de que el id_token llegue al
          backend. Aterriza aquí desde el trampolín POST del backend. */}
      <Route path="/auth/apple/callback" element={<AppleAuthCallback />} />
      {/* PILOTO-1 (firma #79) — el aterrizaje del link de grupo. SIN guard a
          propósito: `ProtectedRoute` mandaría a /login a quien llega sin sesión
          y el código se perdería en el camino, que es justo el caso más
          probable (el link llega por WhatsApp). La página decide sola qué
          pintar según haya sesión o no; ver JoinGroupPage. */}
      <Route path="/join/:code" element={<JoinGroupPage />} />
      {/* post-t7-2c (E1): el programa de invitaciones ya cerro. Una liga vieja de
          invitacion rebota al registro sin banner (en la web y en la app), para
          que no caiga en el 404; sin historial, como el rebote de la raiz. Con
          sesion, el guardia del registro la manda al Panel como siempre. */}
      <Route path="/r/:code" element={<Navigate to="/signup" replace />} />
      {/* Protected routes with sidebar layout */}
      {/* PAGER-2 — EL CARRIL. En el build de APP las CUATRO raíces de la píldora
          (/dashboard, /study, /analytics, /upgrade) renderizan el MISMO elemento:
          <PagerRaices/>, que las lleva vivas lado a lado y activa por `pathname`
          la que está enfrente. El router no pierde una gota de autoridad —sigue
          haciendo match y sigue decidiendo cuál está activa— y como el tipo de
          elemento en esa posición del árbol es el mismo para las cuatro, React
          actualiza en vez de remontar: ése es todo el truco, y es el mismo por el
          que el chasis ya sobrevivía a un cambio de pestaña.
          La SUB-NAVEGACIÓN no se entera: /study/exam|flashcards|adaptive, /pending,
          /settings y el resto siguen siendo sus propios elementos, así que salir a
          una de ellas desmonta el carril entero y volver lo re-monta con las tres
          vecinas DORMIDAS (costo: DOM; red: cero, lo garantiza PAGER-1).
          WEB (`!IS_APP_BUILD`): cada raíz renderiza su página directo, con los
          mismos montajes de siempre. IS_APP_BUILD es un gate de RUNTIME: los dos
          ramales viajan en los dos bundles y el que no aplica va inerte. */}
      {[
        { path: '/dashboard', el: IS_APP_BUILD ? <PagerRaices /> : <Dashboard /> },
        { path: '/pending', el: <PendingPage /> },
        { path: '/study', el: IS_APP_BUILD ? <PagerRaices /> : <StudyCenter /> },
        { path: '/study/exam', el: <ExamSimulator /> },
        { path: '/study/flashcards', el: <Flashcards /> },
        { path: '/study/adaptive', el: <AdaptiveStudy /> },
        { path: '/study/log', el: <StudyLog /> },
        { path: '/analytics', el: IS_APP_BUILD ? <PagerRaices /> : <Analytics /> },
        // tutor-1 (D): /upgrade deja de ser raiz del carril de la app; pinta PricingPage en las dos casas.
        { path: '/upgrade', el: <PricingPage /> },
        // tutor-1 (C): el Tutor. En la app es la cuarta raiz del carril; en la web, su pagina (perezosa). Con la
        // perilla TUTOR apagada la pagina manda al Panel.
        { path: '/tutor', el: IS_APP_BUILD ? <PagerRaices /> : <TutorPage /> },
        // TANDA 12 (firma #182) — LA CONSOLA DE ADMINISTRADOR YA NO ES SOLO DE WEB.
        // Estas dos rutas se registran SIEMPRE, en los dos builds, detras de
        // AdminRoute. Antes el build de app las dejaba fuera por dos vias a la vez
        // (un spread gateado aqui y un reemplazo de modulo en craco.config.js que
        // vaciaba las dos paginas); las dos se retiraron, y el comentario que decia
        // "no admin code ships in the app bundle" dejo de ser verdad con ellas: el
        // codigo admin viaja ahora tambien en el paquete de app, y ese es el costo
        // firmado de poder abrirlas desde el telefono.
        //
        // QUIEN GUARDA LA PUERTA: AdminRoute (components/routeGuards.js), que
        // espera a que la sesion resuelva, manda a login a quien no la tenga y a
        // /dashboard a cualquier cuenta que no sea la de administrador; y desde
        // esta tanda falla CERRADO si la direccion no esta configurada. El
        // servidor contesta por su cuenta, que es el candado que de verdad manda.
        { path: '/admin/errors', el: <AdminRoute><AdminErrorsPage /></AdminRoute> },
        { path: '/admin/dashboard', el: <AdminRoute><AdminDashboard /></AdminRoute> },
        // estilos-1 (Bloque F): aqui llega el boton "Ver como se ve" del correo de formulas. Detras del MISMO AdminRoute.
        { path: '/sb-ctrl/formulas/:docId', el: <AdminRoute><AdminFormulasPage /></AdminRoute> },
        // T1-C3: con examen propio en vuelo no se entra a Ajustes por ninguna
        // vía (link, navigate de PricingPage o deep link). Sin candado el guard
        // es transparente y la ruta se comporta idéntico a antes.
        { path: '/settings', el: <ExamLockedRoute><SettingsPage /></ExamLockedRoute> },
        // Remate de post-t7-2c (R1): "Gana creditos gratis", montada como /settings (el mismo candado de examen, en
        // los dos builds); el invitado rebota a /signup desde la pantalla, como en Ajustes.
        { path: '/earn-credits', el: <ExamLockedRoute><EarnCreditsPage /></ExamLockedRoute> },
        { path: '/manage-documents', el: <ManageDocuments /> },
        // PILOTO-1 (firma #79) — la superficie de PROFESOR. Detrás de
        // `InstitutionalRoute`: una cuenta sin bandera —el 100% de las que
        // existen hoy— rebota a /dashboard antes de montar nada, y el servidor
        // le contestaría 403 igualmente. Se registran en los DOS builds: la
        // pantalla es web-y-app, no hay código de admin de por medio.
        { path: '/groups', el: <InstitutionalRoute><GroupsPage /></InstitutionalRoute> },
        { path: '/groups/:groupId', el: <InstitutionalRoute><GroupDetailPage /></InstitutionalRoute> },
        // T7 tanda 2a (#516): "Avance del grupo". Detras del MISMO guard que sus hermanas; el
        // servidor ademas exige ser el dueno del grupo. Se registra en los dos builds.
        { path: '/groups/:groupId/avance', el: <InstitutionalRoute><GroupAvancePage /></InstitutionalRoute> },
        // PIPE-INST T4 — la pantalla de temas de UN documento del banco. Va detrás
        // del MISMO `InstitutionalRoute` que sus dos hermanas: una cuenta sin
        // bandera rebota a /dashboard antes de montar nada, y el servidor le
        // contestaría 403 igualmente. Se registra en los dos builds, como ellas.
        { path: '/groups/:groupId/bank/:docId', el: <InstitutionalRoute><GroupBankTemasPage /></InstitutionalRoute> },
        // PIPE-INST T5 — la cola de CURACIÓN de UN tema (B-3). Cuarta pantalla de
        // profesor, detrás del MISMO guard que las tres anteriores. Cuelga de la
        // ruta de temas y no al revés porque una cola sin su documento no existe:
        // el `docId` es lo que le da grupo, idioma y permisos.
        { path: '/groups/:groupId/bank/:docId/temas/:temaId', el: <InstitutionalRoute><GroupBankCuracionPage /></InstitutionalRoute> },
        { path: '/support', el: <ContactPage /> },
        // F6.7a — el VISOR de la cadena de entrega. Pantalla normal dentro de
        // AppLayout (hereda el chasis probado: header fijo, bottom nav, safe
        // areas). App-only: la ruta ni siquiera se registra en web (IS_APP_BUILD
        // gatea el spread); una URL /diag/* en web cae al catch-all de abajo.
        ...(IS_APP_BUILD ? [
          { path: '/diag/ready-chain', el: <DiagReadyChainPage /> },
        ] : []),
      ].map(r => (
        <Route key={r.path} path={r.path} element={
          <ProtectedRoute esqueleto={esqueletoDelArranque(r)}>
            <AppLayout>{r.el}</AppLayout>
          </ProtectedRoute>
        } />
      ))}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  // F6.7a — los DOS momentos de lifecycle de la entrega confiable. Ambos usan
  // eventos de window/document que WKWebView sí despacha, sin plugin nativo de
  // por medio.
  //
  // ACTUALIZADO F6.8 T5 — este comentario decía «el censo confirmó que no hay
  // @capacitor/app ni @capacitor/network instalados». De @capacitor/app sigue
  // siendo cierto; de @capacitor/network YA NO: la T5 lo instaló
  // (package.json). Lo que NO cambió es este effect. El sensor nativo no se
  // consulta desde aquí ni desde ninguna pantalla: entra por el oído ÚNICO del
  // proyecto (lib/offline/networkVerdict.js, que lo siembra y lo escucha), y el
  // vigía de red de abajo se suscribe a ESE bus de veredicto, no a `window` ni
  // al plugin (lib/warehouse/converge.js — doctrina del oído único, T5-3).
  // Abrir un segundo oído aquí sería exactamente lo que esa doctrina cerró.
  //
  //   - volver del segundo plano (visibilitychange → visible): el caso donde el
  //     poller estuvo congelado y el servidor terminó sin que nadie mirara;
  //   - recuperar la señal (vigía de red, evento `online`).
  //
  // CERO timers nuevos: los dos son reacciones, no relojes. En web el effect
  // entero es inerte (IS_APP_BUILD) y convergeWarehouse además corta sola.
  useEffect(() => {
    if (!IS_APP_BUILD) return undefined;
    let stopNetworkWatch = () => {};
    let cancelled = false;
    import('./lib/warehouse/converge').then((c) => {
      if (cancelled) return;
      stopNetworkWatch = c.startNetworkWatch();
    }).catch(() => {});

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        // #408 — irse al fondo APAGA la escalera de reintentos: una app que el
        // usuario dejó atrás no tiene por qué seguir tocando la red. La otra
        // mitad del handler (el despertador del cartero) no tiene nada que
        // cancelar, y por eso este camino sale aquí.
        import('./lib/warehouse/converge')
          .then((c) => c.cancelRetryLadder())
          .catch(() => {});
        return;
      }
      // #408 — EL RETORNO DE ESTA CORRIDA YA NO SE TIRA. Traía `readFailed` —"no
      // conseguí la verdad del servidor"— y aquí se perdía, así que volver del
      // segundo plano con la red todavía fría se quedaba con un solo intento y
      // el material esperaba al siguiente evento suelto. Ahora enciende LA MISMA
      // escalera 1.5/4/9/20 s que ya usa la vuelta de la red, con este mismo
      // motivo. La primera lectura no cambia: sigue saliendo como salía.
      import('./lib/warehouse/converge')
        .then((c) => c.convergeWithRetryLadder(c.CONVERGE_REASON.APP_ACTIVE))
        .catch(() => {});
      // F6.6b hotfix (F2) — la BAJADA ya tenía su gancho aquí; la SUBIDA no.
      // Un sobre que quedó esperando offline no puede necesitar que el usuario
      // conteste algo nuevo para salir: volver al frente es disparador
      // legítimo, igual que la señal. No-op en web y pre-corte.
      import('./lib/brain/dispatcher')
        .then((d) => d.dispatchOnWake('app-active'))
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      stopNetworkWatch();
    };
  }, []);

  // TANDA 14 (firma #187) — EL CAMBIO DE RUTA VA CON PRIORIDAD NORMAL.
  //
  // La prop `useTransitions={false}` del router de abajo. Sin ella, el router
  // envuelve su `setState` de ubicación en `React.startTransition`, y una
  // transición de React 19 es interrumpible y DIFERIBLE: puede no aterrizar
  // jamás. Cuando eso pasa, la URL ya avanzó —el `pushState` corre dentro del
  // click del enlace, síncrono— y la pantalla no; y nada del producto la vuelve
  // a empujar: ni el ciclo de visibilidad, ni el regreso de la señal, ni ningún
  // reloj. Peor todavía: ante una suspensión React no reemplaza el contenido
  // visible por un fallback, ESPERA.
  //
  // Es el congelón del 27-ago (boot `c86wr6`, app iOS build 88): dos horas de
  // taps moviendo la dirección contra la misma pantalla, curado sólo matando la
  // app. El censo de esa bitácora dejó este eslabón como el único diferible de
  // toda la cadena tap → pantalla.
  //
  // El enlace NO cambia de camino: su manejador de click ya navegaba síncrono
  // (lee esta misma bandera del contexto, que hasta hoy le llegaba sin valor).
  // Lo único que cambia es la PRIORIDAD del commit de la ubicación.
  //
  // Lo candadea `lib/tanda14.rutaNoDiferible.test.js`, guarda de fuente: el
  // efecto es de prioridad de commit y jsdom no lo puede observar.
  // post-t7-2c (F2, Anexo D.1): la red contra las caidas va por FUERA de todo (tambien del proveedor de sesion), en
  // la web y en la app; `PuenteDeSalida`, dentro del proveedor, le deja el `logout` de la casa para su tercer boton.
  return (
    <RedDeArranque>
    <AuthProvider>
      <PuenteDeSalida />
      <ExamProvider>
      <HelmetProvider>
      <BrowserRouter useTransitions={false}>
        <NotificationListener />
        <PushPuente />
        <GroupBankVigilante />
        <IdleRoutePrefetch />
        <AppRoutes />
        <AuthBootGate />
        {
          // #149d (2026-08-25) — `expand`: la pila de avisos queda SIEMPRE en fila.
          // Sin esta prop sonner la vive colapsada y la abre solo al pasarle el mouse
          // por encima: los avisos se abanican 14 px cada uno, animados 400 ms. Es el
          // unico movimiento por hover que alcanza TODAS las pantallas autenticadas a
          // la vez, porque el Toaster es chasis global. Con `expand` la fila es la de
          // siempre y deja de reaccionar al puntero — que ademas es lo que el guion ya
          // daba por hecho: «salen EN FILA, jamas encimados» (bannersDeTema.test.js,
          // GroupBankTemasPage.t5.test.js). En tactil no cambia nada: sonner ya traia
          // su propia compuerta para ese caso.
          //
          // LA FORMA DEL COMENTARIO NO ES CAPRICHO. Es un contenedor de expresion con
          // comentarios de LINEA porque aqui abajo no cabe uno de bloque: hay dos
          // rutas comodin escritas dentro de comentarios de linea que dejan sendas
          // aperturas huerfanas, y un cierre nuevo les daria pareja — el barrido de
          // `settingsExamLock.test.js` se comeria el registro de rutas entero. El
          // aviso completo esta arriba, junto a los imports.
          //
          // dashboard-rediseno (firma 4): arriba al centro y sin `richColors`. El aspecto del aviso flotante (gris
          // oscuro, el color solo en el icono) y su lugar debajo de la barra viven en `components/ui/sonner.jsx`.
        }
        <Toaster position="top-center" expand />
      </BrowserRouter>
      </HelmetProvider>
      </ExamProvider>
    </AuthProvider>
    </RedDeArranque>
  );
}

export default App;
